/**
 * The panel a rejected module save resolves through.
 *
 * @module components/studio/ModuleConflictPanel
 */

import {
  collidingMimloIds,
  describeConflictPair,
  moduleConflictFields,
  moduleMimloIds,
  takeModuleMappings,
} from "@/components/studio/module-conflict-fields";
import { Alert, Button, EditLockExempt, Heading, ListRow, Panel, Text } from "@/components/ui";
import type { Module } from "@/types";

export interface ModuleConflictPanelProps {
  /** The local draft, as it would be saved. */
  mine: Module;
  /** The module as stored, or undefined when another editor deleted it. */
  theirs: Module | undefined;
  /** The module the draft was read from, so a field only they changed is not offered. */
  base: Module;
  /** Replace one field of the draft with the stored value. */
  onTakeTheirs: (key: string, value: unknown) => void;
  /** The draft's programme map. */
  myMappings: Record<string, string[]>;
  /** The programme map as stored. */
  theirMappings: Record<string, string[]>;
  /** The programme map the draft was read from. */
  baseMappings: Record<string, string[]>;
  /**
   * Take the stored placements of the outcomes both editors moved, into the
   * draft's map and into its base, so the next save moves none of them.
   */
  onTakeTheirMappings: (taken: Record<string, string[]>, base: Record<string, string[]>) => void;
  /** Stop showing the panel without changing the draft. */
  onDismiss: () => void;
}

/**
 * Show what changed underneath a rejected save, and let a person decide.
 *
 * Every differing field is listed with both values. Nothing is applied
 * automatically: keeping the local value is the default, and taking the stored
 * one is a deliberate click per field.
 *
 * @param props - The two copies and the callbacks that resolve between them.
 * @returns The panel.
 */
export function ModuleConflictPanel({
  mine,
  theirs,
  base,
  onTakeTheirs,
  myMappings,
  theirMappings,
  baseMappings,
  onTakeTheirMappings,
  onDismiss,
}: ModuleConflictPanelProps): React.ReactElement {
  const fields = moduleConflictFields(mine, theirs, base);
  // Only this module's outcomes, and only those both editors moved: the
  // server composes the rest, as it does a field only one side changed.
  const colliding = collidingMimloIds(
    baseMappings,
    myMappings,
    theirMappings,
    moduleMimloIds(mine, theirs),
    moduleMimloIds(mine),
  );
  const mappingsDiffer = colliding.size > 0;

  return (
    <div className="mt-5" data-testid="module-conflict-panel">
      <Alert tone="warning">
        {theirs
          ? "Another editor saved this module while you were working. Your changes are still here, unsaved."
          : "Another editor deleted this module while you were working. Saving will not bring it back."}
      </Alert>
      {fields.length > 0 ? (
        <Panel tone="inset" flush className="mt-3">
          <ul className="divide-y divide-base-300">
            {fields.map((field) => {
              const [yourValue, theirValue] = describeConflictPair(field.mine, field.theirs);
              return (
                <li
                  key={field.key}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                  data-testid={`module-conflict-field-${field.key}`}
                >
                  <div className="min-w-0">
                    <Heading level={5}>{field.label}</Heading>
                    <Text as="div" size="sm" tone="muted">
                      Yours: {yourValue} · Theirs: {theirValue}
                    </Text>
                  </div>
                  {/* Named by its field: a list of conflicts otherwise offers the
                  same sentence once per row to anyone reading the controls. */}
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Use theirs for ${field.label}`}
                    onClick={() => onTakeTheirs(field.key, field.theirs)}
                  >
                    Use theirs
                  </Button>
                </li>
              );
            })}
          </ul>
        </Panel>
      ) : null}
      {theirs && mappingsDiffer ? (
        // A placement both editors moved has no finer resolution: this
        // module's placements are taken as stored, or the save is refused again.
        <ListRow
          className="mt-3"
          trailing={
            <Button
              variant="outline"
              size="sm"
              aria-label="Use theirs for Outcome mappings"
              onClick={() =>
                onTakeTheirMappings(
                  takeModuleMappings(myMappings, theirMappings, colliding),
                  takeModuleMappings(baseMappings, theirMappings, colliding),
                )
              }
            >
              Use theirs
            </Button>
          }
          title="Outcome mappings"
          description="Another editor moved this module&rsquo;s outcomes. You cannot save again while you and they placed the same outcome differently. Use theirs to take their placements."
          data-testid="module-conflict-field-mappings"
        />
      ) : null}
      {theirs && fields.length === 0 && !mappingsDiffer ? (
        // A refusal can come from a field this panel has no row for. Saying
        // nothing at all reads as a bug.
        <Text tone="muted" className="mt-3" data-testid="module-conflict-no-fields">
          None of the fields shown here differ. The change was to a field this panel does not list.
          Saving again keeps your copy of what you changed and theirs of the rest.
        </Text>
      ) : null}
      <div className="mt-2">
        {/* Closing takes nothing into the draft: it keeps the reader's copy and
            bases the next save on theirs, the reader's own answer, so the lock
            leaves it in reach. */}
        <EditLockExempt>
          <Button variant="outline" size="sm" onClick={onDismiss}>
            Keep mine and close
          </Button>
        </EditLockExempt>
      </div>
    </div>
  );
}
